// Команды интерфейса: типизированный протокол между разметкой и обработчиком кликов.
// Кнопка несёт data-cmd (JSON команды) и data-k (её вид — для стилей и автотестов); карты несут
// data-zone/data-iid или data-pile — их значение определяет обработчик по зоне. Никаких «магических» data-*.
import type { BossId, BotLevel, CardId, Iid, ManualField, MutatorId, PlayerId, PromptAnswer } from '../types.ts';
import type { HomeTab, Mode, TopTab } from './state.ts';

/** Кнопки плеера записи партии (replay.ts) */
export type ReplayOp = 'exit' | 'prevTurn' | 'prev' | 'toggle' | 'next' | 'nextTurn' | 'speed' | 'log' | 'restart' | 'review';

export type MenuItem = 'log' | 'tokens' | 'trash' | 'manual' | 'rules' | 'mute' | 'install' | 'quit' | 'leave' | 'inbox' | 'changelog' | 'fs';

export type UiCommand =
  // стартовый экран и лобби
  | { k: 'npMinus' } | { k: 'npPlus' } | { k: 'newGame' } | { k: 'mute' } | { k: 'instantToggle' } | { k: 'rebalanceToggle' } | { k: 'rebalanceInfo' } | { k: 'chipsInfo' }
  | { k: 'create' } | { k: 'join' } | { k: 'resume' } | { k: 'forget' } | { k: 'leave' }
  | { k: 'start' } | { k: 'addBot'; level: BotLevel } | { k: 'roomRebalance'; on: boolean } | { k: 'roomOpen'; on: boolean } | { k: 'copy'; link: string } | { k: 'botToggle'; i: number }
  | { k: 'continue' } | { k: 'delete' } | { k: 'toStart' } | { k: 'home'; tab: HomeTab } | { k: 'quickBots' } | { k: 'quickN'; n: number } | { k: 'quickLevel'; level: BotLevel } | { k: 'newConfirm'; quick: boolean; boss?: BossId }
  // рейтинги: номинация на экране
  | { k: 'topTab'; tab: TopTab } | { k: 'endToTop' }
  // сид дня: начать попытку, зал славы с экрана итога, запись партии дня; мутаторы партии — справка
  | { k: 'quickBossToggle' } | { k: 'mutToggle'; id: MutatorId } | { k: 'mutatorsHelp' } | { k: 'duelStart'; boss: BossId } | { k: 'dailyStart' } | { k: 'endToDaily' } | { k: 'dailyGame'; game: number; name: string; score: number; won: boolean } | { k: 'mutatorInfo' }
  // игра по переписке: число мест, создать, войти в набор, открыть свою партию, обновить список; уведомления устройства
  | { k: 'corrSize'; n: number } | { k: 'corrCreate' } | { k: 'corrJoin'; code: string } | { k: 'corrOpen'; code: string } | { k: 'corrRefresh' }
  | { k: 'pushEnable' } | { k: 'pushDisable' } | { k: 'pushRemove'; id: string } | { k: 'pushTest' } | { k: 'corrGo'; code: string }
  // профиль и связывание устройств
  | { k: 'profile'; tab?: 'games' | 'devices' } | { k: 'profileTab'; tab: 'games' | 'devices' } | { k: 'playerProfile'; id: string }
  | { k: 'playedGame'; game: number; owner: 'me' | 'other' } | { k: 'lossPage'; owner: 'me' | 'other'; offset: number }
  | { k: 'achAll'; owner: 'me' | 'other' }
  | { k: 'replay'; op: ReplayOp } | { k: 'replayAt'; idx: number }
  | { k: 'linkShow' } | { k: 'linkEnter' } | { k: 'linkRedeem' } | { k: 'linkLeave'; confirmed: boolean } | { k: 'linkCancel' }
  // матчмейкинг и уведомления: войти в открытое лобби, позвать игрока (id клиента), ящик, что нового
  | { k: 'joinLobby'; code: string } | { k: 'invite'; id: string } | { k: 'inbox' } | { k: 'changelog' }
  | { k: 'inboxClear' } | { k: 'inboxRemove'; id: string } | { k: 'newsToggle' } | { k: 'sysNotify' }
  // подготовка
  | { k: 'setupPass' } | { k: 'trait'; id: string } | { k: 'fam'; cid: string } | { k: 'setupDone' }
  // экран передачи телефона и итоговый
  | { k: 'pass' } | { k: 'log' } | { k: 'review' }
  // модальные окна и меню
  | { k: 'close' } | { k: 'toggleOrig' } | { k: 'menuOpen' } | { k: 'menu'; item: MenuItem } | { k: 'installNow' } | { k: 'installedFs' }
  | { k: 'adjust'; pid: PlayerId; field: ManualField | 'draw'; delta: number }
  | { k: 'goBuy' } | { k: 'endConfirm' }
  // ответы на запросы
  | { k: 'ans'; value: PromptAnswer } | { k: 'opt'; id: string } | { k: 'pid'; pid: PlayerId | null }
  | { k: 'numStep'; d: number } | { k: 'numOk' } | { k: 'numPick'; n: number } | { k: 'cardsOk' }
  | { k: 'sheetToggle' } | { k: 'cancel' }
  // игровой экран
  | { k: 'fs' } | { k: 'mode'; mode: Mode } | { k: 'tokens' } | { k: 'score' } | { k: 'deck' } | { k: 'discard' } | { k: 'trash' }
  | { k: 'handToggle' } | { k: 'endTurn' }
  | { k: 'play'; iid: Iid } | { k: 'activate'; iid: Iid } | { k: 'activateToken'; id: string }
  | { k: 'buy'; iid: Iid } | { k: 'buyChaos' } | { k: 'bc'; d: number }
  | { k: 'zoom'; iid: Iid } | { k: 'zoomCid'; cid: CardId } | { k: 'unraise' } | { k: 'player'; pid: PlayerId };

export type CommandKind = UiCommand['k'];

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escAttr = (s: string): string => s.replace(/[&<>"']/g, (m) => ESC[m] ?? m);

/** Атрибуты кнопки с командой: `<button ${cmd({ k: 'play', iid })}>` */
export function cmd(c: UiCommand): string {
  return `data-cmd="${escAttr(JSON.stringify(c))}" data-k="${c.k}"`;
}

/** То же, что cmd(), но пропсами для preact-компонентов (renderToString сам экранирует значения) */
export function cmdProps(c: UiCommand): Record<string, string> {
  return { 'data-cmd': JSON.stringify(c), 'data-k': c.k };
}

/** Команда из элемента (или его ближайшего предка с командой) */
export function readCmd(el: Element): UiCommand | null {
  const host = el.closest<HTMLElement>('[data-cmd]');
  const raw = host?.dataset['cmd'];
  if (!raw) return null;
  try {
    const c = JSON.parse(raw) as unknown;
    if (c && typeof c === 'object' && typeof (c as { k?: unknown }).k === 'string') return c as UiCommand;
  } catch {
    /* повреждённая разметка — не команда */
  }
  return null;
}
